import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { GameState, Player, Cup, CupFixture, DisplayFixture, TeamInLeague } from '../../domain/entities';
import { FixturesHelper } from '../../services/FixturesHelper';
import { MatchDetailsModal } from '../shared/MatchDetailsModal';
import { soundService } from '../../services/SoundService';
import { View } from '../../App';
import { AdPlaceholder } from '../shared/AdPlaceholder';
import { LeaguePositionChart } from '../shared/LeaguePositionChart';
import { AllFixturesView } from './dashboard/AllFixturesView';
import { CompetitionsHelpModal } from '../shared/CompetitionsHelpModal';
import { TeamBadge } from '../shared/TeamBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophy, faAmbulance } from '@fortawesome/free-solid-svg-icons';
import { getLeagueColor } from '../../utils/iconUtils';
import './CompetitionsScreen.css';
import { useTableFilters } from '../../hooks/useTableFilters';
import { TableHeader } from '../shared/TableHeader';
import { AdvancedFilterModal } from '../shared/AdvancedFilterModal';

const getCupEmoji = (cupName: string, cupNames: string[]) => {
    if (cupName === cupNames[0]) {
        return <FontAwesomeIcon icon={faTrophy} style={{ color: '#FFD700', marginRight: '6px' }} />; // Gold trophy - Pixel Cup
    } else if (cupName === cupNames[1]) {
        return <FontAwesomeIcon icon={faTrophy} style={{ color: '#C0C0C0', marginRight: '6px' }} />; // Silver trophy - Junior Cup
    }
    return null;
};

interface PlayerWithTeam extends Player {
    teamName: string;
}

interface StatisticsTableProps {
    players: PlayerWithTeam[];
    statKey: keyof Player;
    statTitle: string;
    pushView: (view: View) => void;
    playerTeamName: string;
    tableId: string;
}

const StatisticsTable: React.FC<StatisticsTableProps> = ({ players, statKey, statTitle, pushView, playerTeamName, tableId }) => {
    const {
        filters: tableFilters,
        updateFilter,
        clearFilters,
        isFilterModalOpen,
        setIsFilterModalOpen,
        hasActiveFilters
    } = useTableFilters(tableId, { search: '', sortBy: '' });

    const sortedPlayers = useMemo(() => {
        return [...players]
            .filter(p => (p[statKey] as number) > 0)
            .filter(p => p.name.toLowerCase().includes(tableFilters.search.toLowerCase()))
            .sort((a, b) => (b[statKey] as number) - (a[statKey] as number))
            .slice(0, 50);
    }, [players, statKey, tableFilters.search]);

    const handlePlayerClick = (player: Player) => {
        soundService.playClick();
        pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
    };

    const handleTeamClick = (e: React.MouseEvent, teamName: string) => {
        e.stopPropagation();
        soundService.playClick();
        pushView({ type: 'teamDetail', title: `Team: ${teamName}`, teamName });
    };

    return (
        <>
            <TableHeader
                searchValue={tableFilters.search}
                onSearchChange={(value) => updateFilter('search', value)}
                searchPlaceholder="Search player names..."
                sortValue="name"
                onSortChange={() => {}}
                sortOptions={[]}
                onFilterClick={() => setIsFilterModalOpen(true)}
                onClearFilters={clearFilters}
                hasActiveFilters={hasActiveFilters}
            />
            <div className="table-wrapper">
            <table className="table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Player</th>
                        <th>Team</th>
                        <th style={{textAlign: 'center'}}>{statTitle}</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedPlayers.length === 0 && (
                        <tr><td colSpan={4} style={{textAlign: 'center'}}>No data available.</td></tr>
                    )}
                    {sortedPlayers.map((player, index) => (
                        <tr key={player.id} className={player.teamName === playerTeamName ? 'highlight' : ''}>
                            <td>{index + 1}</td>
                            <td>
                                {player.injury && <FontAwesomeIcon icon={faAmbulance} className="negative" title={`${player.injury.type} Injury (${player.injury.duration} wks)`} style={{ marginRight: '4px' }} />}
                                <button className="link-style" onClick={() => handlePlayerClick(player)}>{player.name}</button>
                            </td>
                            <td>
                                <button className="link-style" onClick={(e) => handleTeamClick(e, player.teamName)}>
                                    <span className={player.teamName === playerTeamName ? 'player-team-name' : ''}>{player.teamName}</span>
                                </button>
                            </td>
                            <td style={{textAlign: 'center', fontWeight: 'bold'}}>{player[statKey] as number}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
            <AdvancedFilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                filters={tableFilters}
                onFilterChange={updateFilter}
                onClearFilters={clearFilters}
                filterOptions={[]}
                title={`${statTitle} Filters`}
            />
        </>
    );
};

const DisciplinaryTable: React.FC<{ players: PlayerWithTeam[], pushView: (view: View) => void, playerTeamName: string, tableId: string }> = ({ players, pushView, playerTeamName, tableId }) => {
    const {
        filters: tableFilters,
        updateFilter,
        clearFilters,
        isFilterModalOpen,
        setIsFilterModalOpen,
        hasActiveFilters
    } = useTableFilters(tableId, { search: '', sortBy: '' });

    const sortedPlayers = useMemo(() => {
        return [...players]
            .map(p => ({ ...p, disciplinaryPoints: (p.yellowCards * 1) + (p.redCards * 3) }))
            .filter(p => p.disciplinaryPoints > 0)
            .filter(p => p.name.toLowerCase().includes(tableFilters.search.toLowerCase()))
            .sort((a, b) => b.disciplinaryPoints - a.disciplinaryPoints || b.redCards - a.redCards)
            .slice(0, 50);
    }, [players, tableFilters.search]);

    const handlePlayerClick = (player: Player) => {
        soundService.playClick();
        pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
    };

    const handleTeamClick = (e: React.MouseEvent, teamName: string) => {
        e.stopPropagation();
        soundService.playClick();
        pushView({ type: 'teamDetail', title: `Team: ${teamName}`, teamName });
    };

    return (
        <>
            <TableHeader
                searchValue={tableFilters.search}
                onSearchChange={(value) => updateFilter('search', value)}
                searchPlaceholder="Search player names..."
                sortValue="name"
                onSortChange={() => {}}
                sortOptions={[]}
                onFilterClick={() => setIsFilterModalOpen(true)}
                onClearFilters={clearFilters}
                hasActiveFilters={hasActiveFilters}
            />
            <div className="table-wrapper">
            <table className="table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Player</th>
                        <th>Team</th>
                        <th style={{textAlign: 'center'}}>YC</th>
                        <th style={{textAlign: 'center'}}>RC</th>
                        <th style={{textAlign: 'center'}}>Pts</th>
                    </tr>
                </thead>
                <tbody>
                     {sortedPlayers.length === 0 && (
                        <tr><td colSpan={6} style={{textAlign: 'center'}}>No data available.</td></tr>
                    )}
                    {sortedPlayers.map((player, index) => (
                        <tr key={player.id} className={player.teamName === playerTeamName ? 'highlight' : ''}>
                            <td>{index + 1}</td>
                            <td>
                                {player.injury && <FontAwesomeIcon icon={faAmbulance} className="negative" title={`${player.injury.type} Injury (${player.injury.duration} wks)`} style={{ marginRight: '4px' }} />}
                                <button className="link-style" onClick={() => handlePlayerClick(player)}>{player.name}</button>
                            </td>
                             <td>
                                <button className="link-style" onClick={(e) => handleTeamClick(e, player.teamName)}>
                                    <span className={player.teamName === playerTeamName ? 'player-team-name' : ''}>{player.teamName}</span>
                                </button>
                            </td>
                            <td className="stat-table-cell-yellow">{player.yellowCards}</td>
                            <td className="stat-table-cell-red">{player.redCards}</td>
                            <td className="stat-table-cell-points">{(player.yellowCards * 1) + (player.redCards * 3)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
            <AdvancedFilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                filters={tableFilters}
                onFilterChange={updateFilter}
                onClearFilters={clearFilters}
                filterOptions={[]}
                title="Disciplinary Filters"
            />
        </>
    );
};

const LeagueTableView = ({ gameState, pushView, initialLeague }: { gameState: GameState, pushView: (view: View) => void, initialLeague?: string }) => {
    const { leagues, playerTeamName, config } = gameState;
    const [selectedLeague, setSelectedLeague] = useState(initialLeague || gameState.playerLeagueName);
    const [subTab, setSubTab] = useState<'table' | 'positions' | 'statistics'>('table');
    const [statsTab, setStatsTab] = useState<'scorers' | 'assists' | 'disciplinary'>('scorers');
    const leagueNames = config.LEAGUE_NAMES;

    const league = leagues[selectedLeague];

    const getRowClass = (index: number) => {
        const leagueConfig = config.LEAGUE_CONFIG[selectedLeague];
        if (!leagueConfig) return '';
        if (leagueConfig.promotion > 0 && index < leagueConfig.promotion) return 'promotion-zone';
        const leagueSize = leagues[selectedLeague].teams.length;
        if (leagueConfig.relegation > 0 && index >= leagueSize - leagueConfig.relegation) return 'relegation-zone';
        return '';
    }

    const playersInLeague = useMemo(() => {
        if (!league) return [];
        return league.teams.flatMap((team: TeamInLeague) =>
            team.squad.map(player => ({ ...player, teamName: team.name }))
        );
    }, [selectedLeague, leagues]);

    const handleViewTeam = (teamName: string) => {
        soundService.playClick();
        pushView({ type: 'teamDetail', title: `Team: ${teamName}`, teamName });
    };

    const handleSelectLeague = (name: string) => {
        soundService.playClick();
        setSelectedLeague(name);
    }

    return (
        <div className="panel panel-no-border-margin">
            <div className="panel-header">League</div>
            <div className="league-selector tabs" style={{marginBottom: 0}}>
                {leagueNames.map(name => (
                    <button key={name} onClick={() => handleSelectLeague(name)} className={`button tab-button ${selectedLeague === name ? 'active' : ''}`}>
                        <FontAwesomeIcon icon={faTrophy} style={{ color: getLeagueColor(name, leagueNames), marginRight: '6px' }} />{name}
                    </button>
                ))}
            </div>

            <div className="sub-tabs tabs">
                <button className={`tab-button ${subTab === 'table' ? 'active' : ''}`} onClick={() => { soundService.playClick(); setSubTab('table'); }}>Table</button>
                <button className={`tab-button ${subTab === 'positions' ? 'active' : ''}`} onClick={() => { soundService.playClick(); setSubTab('positions'); }}>Positions</button>
                <button className={`tab-button ${subTab === 'statistics' ? 'active' : ''}`} onClick={() => { soundService.playClick(); setSubTab('statistics'); }}>Statistics</button>
            </div>

            {subTab === 'table' && league && (
                <div className="table-wrapper">
                    <table className="table">
                    <thead>
                        <tr><th>Pos</th><th>Team</th><th title="Games Played">P</th><th title="Wins">W</th><th title="Draws">D</th><th title="Losses">L</th><th title="Goal Difference">GD</th><th title="Points">Pts</th></tr>
                    </thead>
                    <tbody>
                        {league.teams.map((team, index) => {
                            const gd = team.goalsFor - team.goalsAgainst;
                            return (
                                <tr key={team.name} className={`${team.name === playerTeamName ? 'highlight' : ''} ${getRowClass(index)}`}>
                                    <td className="text-secondary">{index + 1}</td>
                                    <td><button className="link-style" onClick={() => handleViewTeam(team.name)}><span className={team.name === playerTeamName ? 'player-team-name' : ''}>{team.badge && <TeamBadge badge={team.badge} color={team.colors.primary} size="sm" />}{team.name}</span></button></td>
                                    <td className="text-secondary">{team.played}</td>
                                    <td className="text-secondary">{team.won}</td>
                                    <td className="text-secondary">{team.drawn}</td>
                                    <td className="text-secondary">{team.lost}</td>
                                    <td className={gd > 0 ? 'positive' : gd < 0 ? 'negative' : 'text-secondary'}>{gd > 0 ? `+${gd}` : gd}</td>
                                    <td className="points-column">{team.points}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                    </table>
                </div>
            )}
            {subTab === 'positions' && league && (
                <LeaguePositionChart teams={league.teams} playerTeamName={playerTeamName} pushView={pushView} />
            )}
            {subTab === 'statistics' && (
                <>
                    <div className="sub-tabs tabs" style={{marginBottom: '16px'}}>
                        <button className={`tab-button ${statsTab === 'scorers' ? 'active' : ''}`} onClick={() => { soundService.playClick(); setStatsTab('scorers'); }}>Top Scorers</button>
                        <button className={`tab-button ${statsTab === 'assists' ? 'active' : ''}`} onClick={() => { soundService.playClick(); setStatsTab('assists'); }}>Most Assists</button>
                        <button className={`tab-button ${statsTab === 'disciplinary' ? 'active' : ''}`} onClick={() => { soundService.playClick(); setStatsTab('disciplinary'); }}>Disciplinary</button>
                    </div>
                    {statsTab === 'scorers' && <StatisticsTable players={playersInLeague} statKey='goalsScored' statTitle='Goals' pushView={pushView} playerTeamName={playerTeamName} tableId={`stats-scorers-${selectedLeague}`} />}
                    {statsTab === 'assists' && <StatisticsTable players={playersInLeague} statKey='assists' statTitle='Assists' pushView={pushView} playerTeamName={playerTeamName} tableId={`stats-assists-${selectedLeague}`} />}
                    {statsTab === 'disciplinary' && <DisciplinaryTable players={playersInLeague} pushView={pushView} playerTeamName={playerTeamName} tableId={`stats-disciplinary-${selectedLeague}`} />}
                </>
            )}
        </div>
    );
};

interface CupViewProps {
    cup: Cup;
    gameState: GameState;
    pushView: (view: View) => void;
    isAiPunditEnabled: boolean;
    showRatingsOnMatchReport: boolean;
    onCacheAiMatchReport: (fixture: DisplayFixture, report: { title: string; message: string; }) => void;
}

const CupView: React.FC<CupViewProps> = ({ cup, gameState, pushView, isAiPunditEnabled, showRatingsOnMatchReport, onCacheAiMatchReport }) => {
    const [selectedFixture, setSelectedFixture] = useState<DisplayFixture | null>(null);
    const getPlayerById = useCallback((id: number): Player | undefined => {
        for (const leagueName in gameState.leagues) {
            const league = gameState.leagues[leagueName];
            for (const team of league.teams) {
                const player = team.squad.find(p => p.id === id);
                if (player) return player;
            }
        }
        return undefined;
    }, [gameState.leagues]);

    const getPlayerTeamResultClass = (fixture: CupFixture): string => {
        if (!fixture.result) return '';
        const { home, away, result } = fixture;
        const { homeScore, awayScore } = result;
        const { playerTeamName } = gameState;

        if (home !== playerTeamName && away !== playerTeamName) {
            return ''; // Not a player team fixture
        }

        if (homeScore === awayScore) {
            return ''; // Draws are neutral
        }

        if (home === playerTeamName) {
            return homeScore > awayScore ? 'positive' : 'negative';
        } else { // away === playerTeamName
            return awayScore > homeScore ? 'positive' : 'negative';
        }
    };

    if (!cup) {
        return <div className="panel-content"><p>This cup is not active in the current season.</p></div>
    }

    const handleFixtureClick = (fixture: CupFixture) => {
        if (!fixture.result) return;
        soundService.playClick();
        setSelectedFixture({ ...fixture, type: 'cup', competition: cup.name });
    }
    
    const handleTeamClick = (e: React.MouseEvent, teamName: string) => {
        e.stopPropagation();
        soundService.playClick();
        pushView({ type: 'teamDetail', title: `Team: ${teamName}`, teamName });
    };

    return (
        <div className="panel panel-no-border-margin">
            <div className="panel-header">{cup.name}</div>
            {cup.winner && <div className="panel-content" style={{textAlign: 'center'}}><h3>🏆 Winner: {cup.winner} 🏆</h3></div>}
            
            {[...cup.rounds].reverse().map(round => (
                <div key={round.name}>
                    <div className="panel-header" style={{backgroundColor: 'var(--primary-color)', color: 'var(--text-color)', textTransform: 'none', fontSize: '1em' }}>{round.name} (Week {round.week})</div>
                    <div className="table-wrapper">
                        <table className="table">
                            <thead>
                                <tr><th>Home</th><th>Away</th><th style={{textAlign: 'center'}}>Result</th></tr>
                            </thead>
                            <tbody>
                                {FixturesHelper.getAllCupFixtures(gameState.fixtures, cup.name).filter(f => f.roundName === round.name).map((f, i) => (
                                    <tr key={i} className={`${f.result ? 'clickable-fixture' : ''} ${f.home === gameState.playerTeamName || f.away === gameState.playerTeamName ? 'highlight' : ''}`} onClick={() => handleFixtureClick(f)}>
                                        <td className={f.result && f.result.homeScore > f.result.awayScore ? 'positive' : ''}>
                                            <button className="link-style" onClick={(e) => handleTeamClick(e, f.home)}><span className={f.home === gameState.playerTeamName ? 'player-team-name' : ''}>{f.home}</span></button>
                                        </td>
                                        <td className={f.result && f.result.awayScore > f.result.homeScore ? 'positive' : ''}>
                                             {f.away !== 'BYE' ? <button className="link-style" onClick={(e) => handleTeamClick(e, f.away)}><span className={f.away === gameState.playerTeamName ? 'player-team-name' : ''}>{f.away}</span></button> : 'BYE'}
                                        </td>
                                        <td style={{textAlign: 'center'}} className={getPlayerTeamResultClass(f)}>{f.result ? `${f.result.homeScore} - ${f.result.awayScore}` : '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}

            {selectedFixture && (
                <MatchDetailsModal
                    isOpen={!!selectedFixture}
                    onClose={() => setSelectedFixture(null)}
                    fixture={selectedFixture}
                    getPlayerById={getPlayerById}
                    gameState={gameState}
                    pushView={pushView}
                    isAiPunditEnabled={isAiPunditEnabled}
                    showRatingsOnMatchReport={showRatingsOnMatchReport}
                    onCacheAiMatchReport={onCacheAiMatchReport}
                />
            )}
        </div>
    );
};

interface CompetitionsScreenProps {
    gameState: GameState;
    pushView: (view: View) => void;
    adsEnabled: boolean;
    isAiPunditEnabled: boolean;
    showRatingsOnMatchReport: boolean;
    onCacheAiMatchReport: (fixture: DisplayFixture, report: { title: string; message: string; }) => void;
    initialLeague?: string;
    activeTab?: string;
}

export const CompetitionsScreen = ({ gameState, pushView, adsEnabled, isAiPunditEnabled, showRatingsOnMatchReport, onCacheAiMatchReport, initialLeague, activeTab: initialActiveTab }: CompetitionsScreenProps) => {
    const [activeTab, setActiveTab] = useState(initialActiveTab || 'all_fixtures');
    const [activeCupTab, setActiveCupTab] = useState<string | null>(null);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const cupNames = gameState.config.CUP_NAMES;

    // Update activeTab when prop changes
    useEffect(() => {
        if (initialActiveTab) {
            setActiveTab(initialActiveTab);
        }
    }, [initialActiveTab]);

    // Initialize active cup tab on first render
    if (activeCupTab === null && cupNames.length > 0) {
        setActiveCupTab(cupNames[0]);
    }

    const handleTabClick = (tab: string) => {
        soundService.playClick();
        setActiveTab(tab);
    }

    const handleCupTabClick = (cupName: string) => {
        soundService.playClick();
        setActiveCupTab(cupName);
    }

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1><FontAwesomeIcon icon={faTrophy} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Competitions</h1>
                <button
                    className="button"
                    onClick={() => setIsHelpOpen(true)}
                    style={{ padding: '8px 12px', fontSize: '0.9em' }}
                    title="Help"
                >
                    Help
                </button>
            </div>
            <p style={{ marginBottom: '24px', opacity: 0.9 }}>
                View all active leagues and cup competitions. Track standings, fixtures, and your progress across multiple tournaments.
            </p>

            <div className="tabs">
                <button className={`tab-button ${activeTab === 'league' ? 'active' : ''}`} onClick={() => handleTabClick('league')}><FontAwesomeIcon icon={faTrophy} style={{ marginRight: '6px' }} />Leagues</button>
                <button className={`tab-button ${activeTab === 'cups' ? 'active' : ''}`} onClick={() => handleTabClick('cups')}><FontAwesomeIcon icon={faTrophy} style={{ marginRight: '6px' }} />Cups</button>
                <button className={`tab-button ${activeTab === 'all_fixtures' ? 'active' : ''}`} onClick={() => handleTabClick('all_fixtures')}>All Fixtures</button>
            </div>
            {adsEnabled && <AdPlaceholder />}
            {activeTab === 'all_fixtures' && <AllFixturesView gameState={gameState} pushView={pushView} isAiPunditEnabled={isAiPunditEnabled} showRatingsOnMatchReport={showRatingsOnMatchReport} onCacheAiMatchReport={onCacheAiMatchReport} />}
            {activeTab === 'league' && <LeagueTableView gameState={gameState} pushView={pushView} initialLeague={initialLeague} />}
            {activeTab === 'cups' && (
                <>
                    <div className="tabs table-tabs">
                        {cupNames.map(name => (
                            <button key={name} className={`tab-button ${activeCupTab === name ? 'active' : ''}`} onClick={() => handleCupTabClick(name)}>
                                {getCupEmoji(name, cupNames)}{name}
                            </button>
                        ))}
                    </div>
                    {activeCupTab && <CupView cup={gameState.cups[activeCupTab]} gameState={gameState} pushView={pushView} isAiPunditEnabled={isAiPunditEnabled} showRatingsOnMatchReport={showRatingsOnMatchReport} onCacheAiMatchReport={onCacheAiMatchReport} />}
                </>
            )}

            <CompetitionsHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
        </>
    );
};
